/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import * as AdaptiveCards from "adaptivecards";
import React, { useEffect } from "react";
import { logError } from "../../telemetry";

export type AdaptiveCardProps<T> = {
    /**
     * The source object representing the Adaptive Card to be rendered.
     */
    source: T;

    /**
     * Optional host configuration for rendering the Adaptive Card.
     */
    hostConfig?: AdaptiveCards.HostConfig;
};

/**
 * Renders an Adaptive Card based on the provided source and optional host configuration.
 * @param props - The properties for the Adaptive Card component.
 * @returns A rendered Adaptive Card element or null if parsing fails.
 */
export const AdaptiveCard: <T, >(props: AdaptiveCardProps<T>) => React.ReactElement | null = ({ source, hostConfig }) => {
    const parentRef = React.useRef<HTMLDivElement>(null);
    const adaptiveCard = new AdaptiveCards.AdaptiveCard();

    if (hostConfig) {
        adaptiveCard.hostConfig = hostConfig;
    }

    useEffect(() => {
        try {
            adaptiveCard.parse(source);
        } catch (error) {
            logError('AdaptiveCardParseError', error instanceof Error ? error.message : String(error));
            return;
        }

        const renderedCard = adaptiveCard.render();

        if (!renderedCard) {
            return;
        }

        if (parentRef.current) {
            parentRef.current.innerHTML = "";
            parentRef.current.appendChild(renderedCard);
        }

    }, [source, parentRef]);

    return <div ref={parentRef} />;
};